import React, { useEffect, useState } from "react"
import {
  Card, CardBody, Row, Col, Table, Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Input, Label, FormGroup,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { toastError, toastSuccess } from "../../helpers/msg_helper"
import { getSettingDefinitions, saveSettingDefinition, deleteSettingDefinition } from "../../services/developer"
import "../Tenancy/tenancy.css"
import "./developer.css"

const TYPES = ["integer", "decimal", "boolean", "select", "date", "string"]
const empty = {
  key: "", label: "", category: "", category_label: "", type: "string",
  default_value: "", unit: "", help: "", options: [], is_group: true, is_company: true,
  is_active: true, sort_order: 0,
}

/**
 * Developer · Settings Manager (fields) — runtime CRUD over the setting_definitions
 * catalog. New fields appear in the Group/Company settings forms.
 */
const FieldManager = () => {
  document.title = "Settings Manager | Everrise"
  const [rows, setRows] = useState([])
  const [categories, setCategories] = useState({})
  const [reload, setReload] = useState(0)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(empty)
  const [optionsText, setOptionsText] = useState("")

  useEffect(() => {
    ;(async () => {
      try {
        const r = await getSettingDefinitions()
        setRows(r?.data || [])
        setCategories(r?.categories || {})
      } catch (e) {
        toastError(e?.message)
      }
    })()
  }, [reload])

  const open = item => {
    const f = item ? { ...empty, ...item } : { ...empty }
    setForm(f)
    setOptionsText((f.options || []).map(o => `${o.value}|${o.label}`).join("\n"))
    setModal(true)
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    try {
      const payload = { ...form }
      if (form.type === "select") {
        payload.options = optionsText
          .split("\n")
          .map(l => l.trim())
          .filter(Boolean)
          .map(l => {
            const [value, label] = l.split("|")
            return { value: (value || "").trim(), label: (label || value || "").trim() }
          })
      } else {
        payload.options = null
      }
      await saveSettingDefinition(form.id, payload)
      toastSuccess("Saved")
      setModal(false)
      setReload(reload + 1)
    } catch (e) {
      toastError(e?.message || (e?.errors && Object.values(e.errors)[0][0]) || "Save failed")
    }
  }

  const remove = async id => {
    if (!window.confirm("Delete this field? Saved values are kept.")) return
    try {
      await deleteSettingDefinition(id)
      toastSuccess("Deleted")
      setReload(reload + 1)
    } catch (e) {
      toastError(e?.message)
    }
  }

  const catOptions = Object.keys(categories || {})

  return (
    <div className="page-content tnt-scope">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center">
          <Breadcrumbs
            title="Developer"
            breadcrumbObject={[
              { title: "Developer", link: "/developer" },
              { title: "Settings Manager", link: "#" },
            ]}
          />
          <Button color="primary" size="sm" onClick={() => open(null)}>
            + Field
          </Button>
        </div>
        <Card className="me-2 px-2 pb-2 main-dashboard-bg">
          <CardBody>
            <Table responsive borderless className="align-middle tnt-table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Label</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Group</th>
                  <th>Company</th>
                  <th>Active</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    <td className="text-muted">{r.key}</td>
                    <td>{r.label}</td>
                    <td>{r.category_label || r.category}</td>
                    <td>{r.type}</td>
                    <td>{r.is_group ? "✓" : ""}</td>
                    <td>{r.is_company ? "✓" : ""}</td>
                    <td>
                      {r.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </td>
                    <td className="text-nowrap">
                      <i className="uil-pen action-icon" role="button" title="Edit" onClick={() => open(r)} />
                      <i className="uil-trash-alt action-icon danger" role="button" title="Delete" onClick={() => remove(r.id)} />
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan="8" className="text-center text-muted">
                      No fields defined
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
        <ModalHeader toggle={() => setModal(false)}>{form.id ? "Edit" : "New"} Field</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={5}>
              <FormGroup>
                <Label>Key * (e.g. finance.vat_rate)</Label>
                <Input value={form.key} disabled={!!form.id} onChange={e => set("key", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Label *</Label>
                <Input value={form.label} onChange={e => set("label", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Type *</Label>
                <Input type="select" value={form.type} onChange={e => set("type", e.target.value)}>
                  {TYPES.map(t => (
                    <option key={t}>{t}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={5}>
              <FormGroup>
                <Label>Category *</Label>
                <Input list="cat-list" value={form.category} onChange={e => set("category", e.target.value)} />
                <datalist id="cat-list">
                  {catOptions.map(c => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Category label</Label>
                <Input value={form.category_label || ""} onChange={e => set("category_label", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label>Unit</Label>
                <Input value={form.unit || ""} onChange={e => set("unit", e.target.value)} />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={5}>
              <FormGroup>
                <Label>Default value</Label>
                <Input value={form.default_value || ""} onChange={e => set("default_value", e.target.value)} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Sort order</Label>
                <Input type="number" value={form.sort_order} onChange={e => set("sort_order", e.target.value)} />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Help text</Label>
            <Input value={form.help || ""} onChange={e => set("help", e.target.value)} />
          </FormGroup>
          {form.type === "select" && (
            <FormGroup>
              <Label>Options (one per line, value|label)</Label>
              <Input type="textarea" rows={4} value={optionsText} onChange={e => setOptionsText(e.target.value)} />
            </FormGroup>
          )}
          <div className="d-flex gap-4">
            <FormGroup check>
              <Input type="checkbox" checked={!!form.is_group} onChange={e => set("is_group", e.target.checked)} />{" "}
              <Label check>Group scope</Label>
            </FormGroup>
            <FormGroup check>
              <Input type="checkbox" checked={!!form.is_company} onChange={e => set("is_company", e.target.checked)} />{" "}
              <Label check>Company scope</Label>
            </FormGroup>
            <FormGroup check>
              <Input type="checkbox" checked={!!form.is_active} onChange={e => set("is_active", e.target.checked)} />{" "}
              <Label check>Active</Label>
            </FormGroup>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={save}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default FieldManager
